import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/auth";

/**
 * useWhisperMic — Shared microphone hook that records via MediaRecorder,
 * uploads the audio to /api/voice/transcribe (Whisper backend), and calls
 * `onAppend(text)` with the transcribed result.
 *
 * Extracted from Battle.jsx (Feb 2026) so the Studio (TrackSubmit) and any
 * future forms can share the same voice-input flow. Paul's TBI + slow-typing
 * days make voice a real accessibility feature, not a nice-to-have.
 *
 * States: idle | recording | transcribing
 * Returns: { state, status, supported, toggle }
 */
export default function useWhisperMic(onAppend) {
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [state, setState] = useState("idle");
  const [status, setStatus] = useState("");
  const [supported] = useState(
    () =>
      typeof window !== "undefined" &&
      !!navigator.mediaDevices &&
      !!window.MediaRecorder,
  );

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* noop */ }
    try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch { /* noop */ }
  }, []);

  const toggle = async () => {
    if (!supported) {
      setStatus("Mic recording not supported in this browser. Try Chrome or Edge.");
      return;
    }
    if (state === "recording") { stop(); return; }
    if (state === "transcribing") return;

    setStatus("Requesting mic access...");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mime = "audio/webm;codecs=opus";
      if (!window.MediaRecorder.isTypeSupported(mime)) mime = "audio/webm";
      if (!window.MediaRecorder.isTypeSupported(mime)) mime = "";
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch { /* noop */ }
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) { setState("idle"); setStatus("Nothing captured — try again."); return; }
        setState("transcribing");
        setStatus("Whisper listening... transcribing your words");
        try {
          const fd = new FormData();
          fd.append("file", blob, "spit.webm");
          const r = await axios.post(`${API}/voice/transcribe`, fd, { timeout: 90000 });
          const txt = (r.data.text || "").trim();
          if (txt) {
            onAppend(txt);
            setStatus(`✓ Captured: "${txt.slice(0, 80)}${txt.length > 80 ? "..." : ""}"`);
          } else {
            setStatus("Whisper heard silence. Speak up and try again.");
          }
        } catch (e) {
          setStatus("⚠ Whisper failed: " + (e.response?.data?.detail || e.message));
        } finally {
          setState("idle");
        }
      };
      rec.start();
      setState("recording");
      setStatus("● REC — tap again when you're done");
    } catch (e) {
      setState("idle");
      setStatus("Mic blocked or unavailable: " + (e.message || e));
    }
  };

  useEffect(() => () => { stop(); }, [stop]);

  return { state, status, supported, toggle };
}
